import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import {
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useDeleteUserMutation,
  useToggleUserStatusMutation,
  useAddWalletBalanceMutation,
  useGetCountriesQuery,
  useGetCitiesQuery,
} from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import Image from '../../../components/Image';
import DateRangePicker from '../../../components/DateRangePicker';
import ActionButtons from '../../../components/ActionButtons';
import Pagination from '../../../components/Pagination';
import { formatDateOnly } from '../../../utils/helpers';
import { showSuccess, showError } from '../../../utils/toast';
import { useLanguage } from '../../../context/LanguageContext';

const VERIFICATION_OPTIONS = [
  { value: '', label: 'All Verifications' },
  { value: 'Green', label: 'Green' },
  { value: 'Gold', label: 'Gold' },
  { value: 'Unverified', label: 'Unverified' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: '1', label: 'Active' },
  { value: '0', label: 'Deactivated' },
];

const CSV_HEADERS = ['#', 'Name', 'Email', 'Phone', 'Country', 'City', 'Status', 'Created Date'];

function getUserRow(user: any, idx: number): string[] {
  return [
    String(idx + 1),
    user.name || '',
    user.email || '',
    user.phone || '',
    user.country?.name || '',
    user.city?.name || '',
    user.accStatus === 1 ? 'Active' : 'Deactivated',
    user.created_at ? new Date(user.created_at).toLocaleDateString() : '',
  ];
}

function downloadCSV(rows: string[][], filename: string) {
  const content = rows
    .map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob(['\ufeff' + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

type SigninType = '' | 'email' | 'google' | 'apple' | 'guest';

const SIGNIN_TABS: { value: SigninType; label: string; icon: string }[] = [
  { value: '',       label: 'All Users',    icon: 'fas fa-users' },
  { value: 'email',  label: 'Email Login',  icon: 'fas fa-envelope-open' },
  { value: 'google', label: 'Google',       icon: 'fab fa-google' },
  { value: 'apple',  label: 'Apple',        icon: 'fab fa-apple' },
  { value: 'guest',  label: 'Guests',       icon: 'fas fa-user' },
];

const USERS_FILTER_KEY = 'admin_users_filters';
function readUsersFilters() {
  try { return JSON.parse(sessionStorage.getItem(USERS_FILTER_KEY) || '{}'); } catch { return {}; }
}

const AdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const verificationOptions = VERIFICATION_OPTIONS.map(o => ({ ...o, label: t(o.label) }));
  const statusOptions = STATUS_OPTIONS.map(o => ({ ...o, label: t(o.label) }));
  const signinTabs = SIGNIN_TABS.map(tab => ({ ...tab, label: t(tab.label) }));
  const [search, setSearch] = useState<string>(() => readUsersFilters().search ?? '');
  const [daterange, setDaterange] = useState<string>(() => readUsersFilters().daterange ?? '');
  const [verificationBadge, setVerificationBadge] = useState<string>(() => readUsersFilters().verificationBadge ?? '');
  const [accStatus, setAccStatus] = useState<string>(() => readUsersFilters().accStatus ?? '');
  const [countryId, setCountryId] = useState<number | null>(() => readUsersFilters().countryId ?? null);
  const [cityId, setCityId] = useState<number | null>(() => readUsersFilters().cityId ?? null);
  const [signinType, setSigninType] = useState<SigninType>(() => readUsersFilters().signinType ?? '');
  const [page, setPage] = useState<number>(() => readUsersFilters().page ?? 1);
  const [exporting, setExporting] = useState(false);

  // Status confirm modal state
  const [statusConfirmUser, setStatusConfirmUser] = useState<any>(null);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [deactivationReason, setDeactivationReason] = useState('');

  // Wallet modal state
  const [walletUser, setWalletUser] = useState<any>(null);
  const [walletAmount, setWalletAmount] = useState('');
  const [walletNote, setWalletNote] = useState('');

  // Persist filters across navigation
  useEffect(() => {
    sessionStorage.setItem(USERS_FILTER_KEY, JSON.stringify({ search, daterange, verificationBadge, accStatus, countryId, cityId, signinType, page }));
  }, [search, daterange, verificationBadge, accStatus, countryId, cityId, signinType, page]);

  const { data: countriesData } = useGetCountriesQuery({ per_page: 2000 });
  const countries = countriesData?.data?.data || countriesData?.data || [];
  const countryOptions = [
    { value: null, label: t('All Countries') },
    ...countries.map((c: any) => ({ value: c.id, label: (lang === 'ar' ? c.name_ar : c.name) || c.name || c.name_en })),
  ];

  const { data: citiesData } = useGetCitiesQuery({ per_page: 2000, country_id: countryId || undefined });
  const cities = citiesData?.data?.data || citiesData?.data || [];
  const cityOptions = [
    { value: null, label: t('All Cities') },
    ...cities.map((c: any) => ({ value: c.id, label: (lang === 'ar' ? c.name_ar : c.name) || c.name })),
  ];

  const filters = { search, daterange, verification_badge: verificationBadge, acc_status: accStatus || undefined, country_id: countryId, city_id: cityId, signin_type: signinType || undefined, page };
  const { data, isLoading, error, refetch } = useGetUsersQuery(filters);
  const [fetchAllUsers] = useLazyGetUsersQuery();
  const [deleteUser] = useDeleteUserMutation();
  const [toggleUserStatus] = useToggleUserStatusMutation();
  const [addWalletBalance, { isLoading: isAddingWallet }] = useAddWalletBalanceMutation();

  const hasActiveFilters = search || daterange || verificationBadge || accStatus || countryId || cityId;

  const handleClear = () => {
    setSearch('');
    setDaterange('');
    setVerificationBadge('');
    setAccStatus('');
    setCountryId(null);
    setCityId(null);
  };

  const counts = data?.counts || { all: 0, email: 0, google: 0, apple: 0, guest: 0 };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const result = await fetchAllUsers({ search, daterange, verification_badge: verificationBadge, acc_status: accStatus || undefined, country_id: countryId, city_id: cityId, per_page: 20000, page: 1 }).unwrap();
      const allUsers: any[] = result?.data?.data || result?.data || [];
      const rows = [CSV_HEADERS, ...allUsers.map((user, idx) => getUserRow(user, idx))];
      const timestamp = new Date().toISOString().slice(0, 10);
      downloadCSV(rows, `users_${timestamp}.csv`);
    } catch {
      showError(t('Failed to export users. Please try again.'));
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [search, daterange, verificationBadge, accStatus, countryId, cityId, signinType]);

  const handleDelete = async (id: number) => {
    try {
      const result = await deleteUser(id).unwrap();
      showSuccess(result.message || t('User deleted successfully!'));
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to delete user. Please try again.'));
    }
  };

  const openStatusModal = (user: any) => {
    setDeactivationReason('');
    setStatusConfirmUser(user);
  };

  const handleToggleStatus = async () => {
    if (!statusConfirmUser) return;
    const isDeactivating = statusConfirmUser.accStatus === 1;
    const reason = deactivationReason.trim();
    if (isDeactivating && !reason) {
      return showError(t('Please enter a reason for deactivation.'));
    }
    setIsTogglingStatus(true);
    try {
      const newStatus = isDeactivating ? 0 : 1;
      const result = await toggleUserStatus({
        id: statusConfirmUser.id,
        status: newStatus,
        reason: isDeactivating ? reason : undefined,
      }).unwrap();
      showSuccess(result.message || t('User status updated successfully!'));
      setStatusConfirmUser(null);
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to update user status. Please try again.'));
    } finally {
      setIsTogglingStatus(false);
    }
  };

  const openWalletModal = (user: any) => {
    setWalletUser(user);
    setWalletAmount('');
    setWalletNote('');
  };

  const closeWalletModal = () => setWalletUser(null);

  const handleAddWallet = async () => {
    const amount = parseFloat(walletAmount);
    if (!amount || amount <= 0) return showError(t('Please enter a valid amount.'));
    try {
      const result = await addWalletBalance({ id: walletUser.id, amount, note: walletNote || undefined }).unwrap();
      showSuccess(result.message || t('Wallet balance updated!'));
      closeWalletModal();
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || t('Failed to update wallet. Please try again.'));
    }
  };

  if (isLoading) return <PageLoader />;
  if (error) return <div className="alert alert-danger m-5">{t('Error loading users')}</div>;

  const users = data?.data || { data: [], current_page: 1, last_page: 1 };

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">{t('Customers')}</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" className="text-muted text-hover-primary">{t('Home')}</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">{t('Users')}</li>
              </ul>
            </div>
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <button
                type="button"
                className="btn btn-sm btn-light-primary me-3"
                onClick={handleExportCSV}
                disabled={exporting}
              >
                {exporting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {t('Exporting...')}
                  </>
                ) : (
                  <>
                    <span className="svg-icon svg-icon-2">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect opacity="0.3" x="12.75" y="4.25" width="12" height="2" rx="1" transform="rotate(90 12.75 4.25)" fill="currentColor"></rect>
                        <path d="M12.0573 6.11875L13.5203 7.87435C13.9121 8.34457 14.6232 8.37683 15.056 7.94401C15.4457 7.5543 15.4641 6.92836 15.0979 6.51643L12.4974 3.59084C12.0996 3.14332 11.4004 3.14332 11.0026 3.59084L8.40206 6.51643C8.0359 6.92836 8.0543 7.5543 8.44401 7.94401C8.87683 8.37683 9.58785 8.34458 9.9797 7.87435L11.4427 6.11875C11.6026 5.92684 11.8974 5.92684 12.0573 6.11875Z" fill="currentColor"></path>
                        <path opacity="0.3" d="M18.75 8.25H17.75C17.1977 8.25 16.75 8.69772 16.75 9.25C16.75 9.80228 17.1977 10.25 17.75 10.25C18.3023 10.25 18.75 10.6977 18.75 11.25V18.25C18.75 18.8023 18.3023 19.25 17.75 19.25H5.75C5.19772 19.25 4.75 18.8023 4.75 18.25V11.25C4.75 10.6977 5.19771 10.25 5.75 10.25C6.30229 10.25 6.75 9.80228 6.75 9.25C6.75 8.69772 6.30229 8.25 5.75 8.25H4.75C3.64543 8.25 2.75 9.14543 2.75 10.25V19.25C2.75 20.3546 3.64543 21.25 4.75 21.25H18.75C19.8546 21.25 20.75 20.3546 20.75 19.25V10.25C20.75 9.14543 19.8546 8.25 18.75 8.25Z" fill="currentColor"></path>
                      </svg>
                    </span>
                    {t('Export CSV')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            {/* Signin Type Counter Tabs */}
            <div className="row g-4 mb-6">
              {signinTabs.map((tab) => {
                const count = tab.value === '' ? counts.all : counts[tab.value as keyof typeof counts] ?? 0;
                const isActive = signinType === tab.value;
                return (
                  <div key={tab.value} className="col-6 col-md-4 col-lg">
                    <div
                      className={`card border cursor-pointer h-100 ${isActive ? 'border-primary bg-light-primary' : 'border-dashed border-gray-300'}`}
                      onClick={() => { if (tab.value === 'guest') return; setSigninType(tab.value); }}
                      style={{ transition: 'all 0.15s' }}
                    >
                      <div className="card-body py-4 px-5 d-flex align-items-center gap-3">
                        <div className={`d-flex align-items-center justify-content-center rounded-circle ${isActive ? 'bg-primary' : 'bg-light-secondary'}`} style={{ width: 40, height: 40, flexShrink: 0 }}>
                          <i className={`${tab.icon} fs-5 ${isActive ? 'text-white' : 'text-gray-600'}`}></i>
                        </div>
                        <div>
                          <div className={`fw-bold fs-2 lh-1 ${isActive ? 'text-primary' : 'text-gray-800'}`}>{count}</div>
                          <div className="text-muted fs-7 mt-1">{tab.label}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="card">
              <div className="card-header border-0 pt-6">
                <div className="card-title w-100">
                  <div className="d-flex flex-wrap align-items-center gap-3 my-1 w-100">
                    <div className="position-relative">
                      <span className="svg-icon svg-icon-1 position-absolute ms-6" style={{ top: '50%', transform: 'translateY(-50%)' }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="currentColor"></rect>
                          <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="currentColor"></path>
                        </svg>
                      </span>
                      <input
                        type="text"
                        className="form-control form-control-solid w-250px ps-15"
                        placeholder={t('Search Name, Email Or Phone..')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                    <DateRangePicker value={daterange} onChange={setDaterange} placeholder={t('Date Range')} />
                    <div style={{ minWidth: 180 }}>
                      <Select
                        options={statusOptions}
                        value={statusOptions.find(o => o.value === accStatus) || statusOptions[0]}
                        onChange={(opt) => setAccStatus(opt?.value || '')}
                        placeholder={t('All Statuses')}
                        className="react-select-container fs-6"
                        classNamePrefix="react-select"
                      />
                    </div>
                    <div style={{ minWidth: 180 }}>
                      <Select
                        options={verificationOptions}
                        value={verificationOptions.find(o => o.value === verificationBadge) || verificationOptions[0]}
                        onChange={(opt) => setVerificationBadge(opt?.value || '')}
                        placeholder={t('Verification')}
                        className="react-select-container fs-6"
                        classNamePrefix="react-select"
                      />
                    </div>
                    <div style={{ minWidth: 180 }}>
                      <Select
                        isClearable
                        options={countryOptions}
                        value={countryOptions.find(o => o.value === countryId) || null}
                        onChange={(opt) => { setCountryId((opt as any)?.value ?? null); setCityId(null); }}
                        placeholder={t('All Countries')}
                        className="react-select-container fs-6"
                        classNamePrefix="react-select"
                      />
                    </div>
                    <div style={{ minWidth: 180 }}>
                      <Select
                        isClearable
                        options={cityOptions}
                        value={cityOptions.find(o => o.value === cityId) || null}
                        onChange={(opt) => setCityId((opt as any)?.value ?? null)}
                        placeholder={t('All Cities')}
                        className="react-select-container fs-6"
                        classNamePrefix="react-select"
                      />
                    </div>
                    {hasActiveFilters && (
                      <button type="button" onClick={handleClear} className="btn btn-sm btn-light-danger">
                        {t('Clear')}
                      </button>
                    )}
                  </div>
                </div>
                <div className="card-toolbar">
                  <button type="button" className="btn btn-primary" onClick={() => navigate('/admin/users/add')}>
                    <span className="svg-icon svg-icon-2">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect opacity="0.5" x="11.364" y="20.364" width="16" height="2" rx="1" transform="rotate(-90 11.364 20.364)" fill="currentColor"></rect>
                        <rect x="4.36396" y="11.364" width="16" height="2" rx="1" fill="currentColor"></rect>
                      </svg>
                    </span>
                    {t('Add User')}
                  </button>
                </div>
              </div>
              <div className="card-body pt-0">
                <div className="table-responsive">
                  <table className="table align-middle table-hover fs-6 gy-5">
                    <thead>
                      <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                        <th className="w-10px pe-2">#</th>
                        <th className="min-w-125px">{t('Image')}</th>
                        <th className="min-w-125px">{t('Name')}</th>
                        <th className="min-w-125px">{t('Username')}</th>
                        <th className="min-w-125px">{t('Email')}</th>
                        <th className="min-w-125px">{t('Phone')}</th>
                        <th className="min-w-125px">{t('Address')}</th>
                        <th className="min-w-125px">{t('Account Type')}</th>
                        <th className="min-w-125px">{t('Verification')}</th>
                        <th className="min-w-75px">{t('Status')}</th>
                        <th className="min-w-100px">{t('Wallet')}</th>
                        <th className="min-w-125px">{t('Created Date')}</th>
                        <th className="min-w-75px">{t('Actions')}</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600 fs-6">
                      {users.data.length === 0 ? (
                        <tr>
                          <td colSpan={13} className="text-center py-10">
                            <span className="text-muted">{t('No users found')}</span>
                          </td>
                        </tr>
                      ) : (
                        users.data.map((user: any, index: number) => (
                          <tr key={user.id} className="odd cursor-pointer" onClick={() => navigate(`/admin/users/${user.id}`)}>
                            <td>{(users.current_page - 1) * 15 + index + 1}</td>
                            <td>
                              <div className="symbol symbol-40px overflow-hidden me-3">
                                <div className="symbol-label">
                                  <Image
                                    src={user.picture}
                                    alt={user.name}
                                    type="avatar"
                                    className="w-100 h-100 object-fit-cover"
                                  />
                                </div>
                              </div>
                            </td>
                            <td>
                              <div>{user.name}</div>
                            </td>
                            <td>
                              {user.username ? (
                                <span className="text-gray-600 fs-7">@{user.username}</span>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td>{user.email}</td>
                            <td>{user.phone || t('N/A')}</td>
                            <td>{(lang === 'ar' ? user.city?.name_ar : user.city?.name) || user.city?.name || '--'} {(lang === 'ar' ? user.country?.name_ar : user.country?.name) || user.country?.name || '--'}</td>
                            <td>
                              {user.account_type ? (
                                <span className="badge badge-light-primary">{(lang === 'ar' ? user.account_type.title_ar : user.account_type.title) || user.account_type.title}</span>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td>
                              {user.verification_badge && user.verification_badge !== 'Unverified' ? (
                                <span className={`badge badge-light-${user.verification_badge.startsWith('Requested') ? 'warning' : 'success'}`}>
                                  {t(user.verification_badge)}
                                </span>
                              ) : (
                                <span className="badge badge-light-secondary text-dark">{t('Unverified')}</span>
                              )}
                            </td>
                            <td>
                              <span
                                className={`badge badge-light-${user.accStatus === 1 ? 'success' : 'danger'}`}
                                style={{ cursor: 'pointer' }}
                                onClick={(e) => { e.stopPropagation(); openStatusModal(user); }}
                                title={t('Click to toggle status')}
                              >
                                {user.accStatus === 1 ? t('Active') : t('Deactivated')}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <span className="fw-bold text-gray-800">${user.wallet_balance ?? 0}</span>
                                {/* <button
                                  className="btn btn-icon btn-sm btn-light-success"
                                  title="Add wallet balance"
                                  onClick={() => openWalletModal(user)}
                                >
                                  <i className="fas fa-plus fs-7"></i>
                                </button> */}
                              </div>
                            </td>
                            <td>{formatDateOnly(user.created_at)}</td>
                            <td className="text-center">
                              <ActionButtons
                                onView={() => navigate(`/admin/users/${user.id}`)}
                                onEdit={() => navigate(`/admin/users/${user.id}/edit`)}
                                onDelete={() => handleDelete(user.id)}
                                onToggleStatus={() => openStatusModal(user)}
                                status={user.accStatus}
                              />
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {users.data.length > 0 && (
                  <div className="card-footer border-0 pt-6">
                    <Pagination
                      currentPage={users.current_page}
                      lastPage={users.last_page}
                      total={users.total || 0}
                      perPage={users.per_page || 15}
                      onPageChange={(newPage) => setPage(newPage)}
                      showTotal={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Confirm Modal */}
      {statusConfirmUser && (
        <>
          <div className="modal fade show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-body text-center py-8 px-6">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle mx-auto mb-5"
                    style={{
                      width: 64, height: 64,
                      background: statusConfirmUser.accStatus === 1 ? 'rgba(220,53,69,0.1)' : 'rgba(25,135,84,0.1)',
                    }}
                  >
                    <i
                      className={`fas ${statusConfirmUser.accStatus === 1 ? 'fa-user-slash' : 'fa-user-check'} fs-2`}
                      style={{ color: statusConfirmUser.accStatus === 1 ? '#dc3545' : '#198754' }}
                    ></i>
                  </div>
                  <h4 className="fw-bold text-dark mb-2">
                    {statusConfirmUser.accStatus === 1 ? t('Deactivate User?') : t('Activate User?')}
                  </h4>
                  <p className="text-muted fs-6 mb-0">
                    {statusConfirmUser.accStatus === 1
                      ? <>{t('Are you sure you want to deactivate')} <strong>{statusConfirmUser.name}</strong>? {t("They won't be able to access the platform.")}</>
                      : <>{t('Are you sure you want to activate')} <strong>{statusConfirmUser.name}</strong>? {t('They will regain access to the platform.')}</>
                    }
                  </p>

                  {statusConfirmUser.accStatus === 1 && (
                    <div className="text-start mt-6">
                      <label className="form-label fw-semibold required">{t('Reason for deactivation')}</label>
                      <textarea
                        className="form-control form-control-solid"
                        rows={4}
                        maxLength={191}
                        placeholder={t('Describe why this account is being deactivated…')}
                        value={deactivationReason}
                        onChange={(e) => setDeactivationReason(e.target.value)}
                        autoFocus
                      />
                      <div className="text-muted fs-8 mt-2">
                        {t('The user will be notified with this reason.')}
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer justify-content-center border-0 pt-0 pb-6 gap-3">
                  <button
                    type="button"
                    className="btn btn-light px-6"
                    onClick={() => setStatusConfirmUser(null)}
                    disabled={isTogglingStatus}
                  >
                    {t('Cancel')}
                  </button>
                  <button
                    type="button"
                    className={`btn px-6 ${statusConfirmUser.accStatus === 1 ? 'btn-danger' : 'btn-success'}`}
                    onClick={handleToggleStatus}
                    disabled={
                      isTogglingStatus ||
                      (statusConfirmUser.accStatus === 1 && !deactivationReason.trim())
                    }
                  >
                    {isTogglingStatus ? (
                      <><span className="spinner-border spinner-border-sm me-2"></span>{t('Updating…')}</>
                    ) : (
                      statusConfirmUser.accStatus === 1 ? t('Yes, Deactivate') : t('Yes, Activate')
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {/* Add Wallet Balance Modal */}
      {walletUser && (
        <>
          <div className="modal fade show d-block" tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{t('Add Wallet Balance')} — {walletUser.name}</h5>
                  <button type="button" className="btn-close" onClick={closeWalletModal}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-4">
                    <p className="text-muted mb-1">{t('Current Balance')}</p>
                    <p className="fw-bold fs-4 text-gray-800">{walletUser.wallet_balance ?? 0}</p>
                  </div>
                  <div className="mb-4">
                    <label className="form-label required">{t('Amount to Add')}</label>
                    <input
                      type="number"
                      className="form-control form-control-solid"
                      placeholder={t('Enter amount')}
                      min="0.01"
                      step="0.01"
                      value={walletAmount}
                      onChange={(e) => setWalletAmount(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">{t('Note (optional)')}</label>
                    <input
                      type="text"
                      className="form-control form-control-solid"
                      placeholder={t('e.g. Refund, bonus credit…')}
                      value={walletNote}
                      onChange={(e) => setWalletNote(e.target.value)}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light" onClick={closeWalletModal}>
                    {t('Cancel')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleAddWallet}
                    disabled={isAddingWallet || !walletAmount}
                  >
                    {isAddingWallet ? (
                      <><span className="spinner-border spinner-border-sm me-2"></span>{t('Adding…')}</>
                    ) : (
                      t('Add Balance')
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default AdminUsers;
